import React from 'react'
import Slider from 'react-slick'
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch'

import { Modal } from 'components/Modal'
import { Color } from 'constants/color'
import { noteEventEmitter, noteKeys } from 'helpers/events'
import { isFunction, getScaleValue } from 'helpers/miscellaneous'
import { getTime } from 'helpers/time'

import { Controls, NextArrow, PrevArrow } from './components'
import {
    ItemContainer,
    ItemImageContainer,
    Item,
    SliderContainer,
    Details,
    DetailInfo,
    MinimizeIcon,
    ControlContainer,
    FallbackContainer,
} from './styles'

import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'

let currentRotationDegree = 0

const getNextRotationDegreeValue = () => {
    if (currentRotationDegree >= 0 && currentRotationDegree < 90) {
        currentRotationDegree = 90
    } else if (currentRotationDegree >= 90 && currentRotationDegree < 180) {
        currentRotationDegree = 180
    } else if (currentRotationDegree >= 180 && currentRotationDegree < 270) {
        currentRotationDegree = 270
    } else {
        currentRotationDegree = 0
    }

    return currentRotationDegree
}

const maximizedModestyles = {
    modal: {
        maxWidth: '100vw',
        maxHeight: '100vh',
        height: 'inherit',
        margin: 0,
        borderRadius: 0,
        backgroundColor: Color.unknownColor19,
    },
    closeButton: {
        filter: 'brightness(0) invert(1)',
    },
}

const transformOptions = {
    maxScale: 4,
}

// FIXME: modify it later if better solution found for zoom in multiple images in slider
let zoomInHandlers = {}
let zoomOutHandlers = {}

const NextArrowComponent = <NextArrow />
const PrevArrowComponent = <PrevArrow />

const ImageCarouselView = () => {
    const [isModalOpen, setIsModalOpen] = React.useState(false)
    const [images, setImages] = React.useState([])

    const silderCurrentImageRef = React.useRef(null)
    const sliderContainerRef = React.useRef(null)
    const sliderRef = React.useRef(null)

    React.useEffect(() => {
        return () => {
            // reset handlers on unmount
            zoomInHandlers = {}
            zoomOutHandlers = {}
        }
    }, [])

    React.useEffect(() => {
        let timeout
        const openModal = ({ imagesData, currentIndex }) => {
            setImages(imagesData)
            setIsModalOpen(true)

            timeout = setTimeout(() => {
                if (sliderRef.current) {
                    sliderRef.current.slickGoTo(currentIndex)
                }
            }, 100)
        }

        noteEventEmitter.addListener(
            noteKeys.OPEN_FULL_SCREEN_NOTE_MODAL,
            openModal
        )

        return () => {
            noteEventEmitter.removeListener(
                noteKeys.OPEN_FULL_SCREEN_NOTE_MODAL,
                openModal
            )

            if (timeout) {
                clearTimeout(timeout)
            }
        }
    }, [])

    const imagesElement = React.useMemo(() => {
        return images.map(item => (
            <ItemContainer key={item.note.id}>
                <ItemImageContainer>
                    {!item.note.isDeleted ? (
                        <TransformWrapper
                            defaultScale={1}
                            options={transformOptions}
                        >
                            {({ zoomIn, zoomOut }) => {
                                // FIXME: modify this later
                                zoomInHandlers[item.note.id] = zoomIn
                                zoomOutHandlers[item.note.id] = zoomOut

                                return (
                                    <TransformComponent>
                                        <Item
                                            src={item.note.file}
                                            className="note-image"
                                            data-id={item.note.id}
                                        />
                                    </TransformComponent>
                                )
                            }}
                        </TransformWrapper>
                    ) : (
                        <FallbackContainer>
                            This image has been deleted
                        </FallbackContainer>
                    )}
                </ItemImageContainer>

                <Details>
                    <DetailInfo>
                        {item.note.name}
                        {', '}
                    </DetailInfo>
                    <DetailInfo>{getTime(item.note.created)}</DetailInfo>
                </Details>
            </ItemContainer>
        ))
    }, [images, sliderRef])

    const getCurrentImageRef = () => {
        if (sliderContainerRef.current) {
            // first get current active element
            const currentElement = sliderContainerRef.current.getElementsByClassName(
                'slick-current'
            )[0]

            if (currentElement) {
                // now get note image element
                const imageElement = currentElement.getElementsByClassName(
                    'note-image'
                )[0]

                if (silderCurrentImageRef.current) {
                    // reset image style first then after set current item in ref
                    silderCurrentImageRef.current.style.transform = 'none'
                }

                // update current image ref
                silderCurrentImageRef.current = imageElement
            }
        }
    }

    const onClockwiseRotateClick = () => {
        if (silderCurrentImageRef.current) {
            const scale = getScaleValue(silderCurrentImageRef.current)

            silderCurrentImageRef.current.style.transform = `${
                typeof scale === 'string' ? scale : ''
            } rotate(${getNextRotationDegreeValue()}deg)`
        }
    }

    const zoomIn = event => {
        if (silderCurrentImageRef.current) {
            // first get index
            const index = silderCurrentImageRef.current.dataset.id

            if (
                Object.prototype.hasOwnProperty.call(zoomInHandlers, index) &&
                isFunction(zoomInHandlers[index])
            ) {
                // call zoom in function
                zoomInHandlers[index](event)
            }
        }
    }

    const zoomOut = event => {
        if (silderCurrentImageRef.current) {
            // first get index
            const index = silderCurrentImageRef.current.dataset.id

            if (
                Object.prototype.hasOwnProperty.call(zoomOutHandlers, index) &&
                isFunction(zoomOutHandlers[index])
            ) {
                // call zoom out function
                zoomOutHandlers[index](event)
            }
        }
    }

    const onCloseModal = () => {
        setIsModalOpen(false)
    }

    const closeIcon = (
        <MinimizeIcon
            onClick={onCloseModal}
            src="/img/artefacts/whiteboard/toolbar/minimize.svg"
        />
    )

    return (
        <Modal
            isModalOpen={isModalOpen}
            onCloseModal={onCloseModal}
            styles={maximizedModestyles}
            closeIcon={closeIcon}
        >
            <SliderContainer ref={sliderContainerRef}>
                <Slider
                    ref={sliderRef}
                    dots={false}
                    infinite={false}
                    speed={500}
                    slidesToShow={1}
                    slidesToScroll={1}
                    nextArrow={NextArrowComponent}
                    prevArrow={PrevArrowComponent}
                    onInit={getCurrentImageRef}
                    onReInit={getCurrentImageRef}
                    afterChange={getCurrentImageRef}
                >
                    {imagesElement}
                </Slider>
            </SliderContainer>

            <ControlContainer>
                <Controls
                    onZoomIn={zoomIn}
                    onZoomOut={zoomOut}
                    onRotateClick={onClockwiseRotateClick}
                />
            </ControlContainer>
        </Modal>
    )
}

export { ImageCarouselView }
